import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { elementPerClau } from '@/navigation/navigation';
import { useMe } from '@/permisos/MeContext';
import { compleix } from '@/permisos/permisos';
import { useGuardaCanvis } from './canvisContext';
import './ajustos.css';

import { GRUPS_AJUSTOS } from './grups';

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Marc comú de les pantalles d'Ajustos: menú lateral fix amb cercador, agrupat, i la pantalla
 * a la dreta. Els enllaços del menú respecten els canvis pendents (demanen confirmació).
 */
export function AjustosMarc({ children }: { children: ReactNode }) {
  const { t } = useTranslation('administracio');
  const { me } = useMe();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const guarda = useGuardaCanvis();
  const [cerca, setCerca] = useState('');
  const q = normalitza(cerca.trim());

  const grups = GRUPS_AJUSTOS.map((g) => ({
    ...g,
    items: g.items
      .map((clau) => ({ clau, e: elementPerClau(clau) }))
      .filter(({ e }) => e && compleix(me, e.permis))
      .map(({ clau, e }) => ({
        clau,
        ruta: e?.ruta ?? '/admin',
        icona: e?.icona ?? 'gear',
        titol: t(`ajustos.items.${clau}.titol`),
        text: t(`ajustos.items.${clau}.text`),
      }))
      .filter((i) => !q || normalitza(`${i.titol} ${i.text}`).includes(q)),
  })).filter((g) => g.items.length > 0);

  // Element actiu: la ruta més llarga que és prefix de l'adreça actual.
  const actiu = grups
    .flatMap((g) => g.items)
    .filter((i) => pathname === i.ruta || pathname.startsWith(`${i.ruta}/`))
    .sort((a, b) => b.ruta.length - a.ruta.length)[0]?.clau;

  const vesA = (ruta: string) => (ev: MouseEvent) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
    ev.preventDefault();
    if (ruta === pathname) return;
    guarda(() => navigate(ruta));
  };

  return (
    <div className="ajustos-marc">
      <nav className="ajustos-menu" aria-label={t('marc.menu')}>
        <Link
          to="/admin"
          className={`ajustos-menu__inici${pathname === '/admin' ? ' is-actiu' : ''}`}
          aria-current={pathname === '/admin' ? 'page' : undefined}
          onClick={vesA('/admin')}
        >
          <Icona nom="sliders" />
          {t('marc.inici')}
        </Link>
        <label className="search ajustos-menu__cerca">
          <Icona nom="magnifier" />
          <input
            type="search"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder={t('marc.cerca')}
            aria-label={t('marc.cerca')}
          />
        </label>
        {grups.length === 0 ? (
          <p className="ajustos-menu__buit">{t('marc.senseResultats')}</p>
        ) : null}
        {grups.map((g) => (
          <div key={g.clau} className="ajustos-menu__grup">
            <div className="ajustos-menu__titol">{t(`marc.grups.${g.clau}`)}</div>
            {g.items.map((i) => (
              <Link
                key={i.clau}
                to={i.ruta}
                className={`ajustos-menu__item${actiu === i.clau ? ' is-actiu' : ''}`}
                aria-current={actiu === i.clau ? 'page' : undefined}
                data-ajust={i.clau}
                title={i.text}
                onClick={vesA(i.ruta)}
              >
                <Icona nom={i.icona} />
                <span className="truncate">{i.titol}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>
      <div className="ajustos-marc__pantalla">{children}</div>
    </div>
  );
}
